import { View } from 'web-mojo';

/**
 * ContactOperatorModal — contact an operator via in-app DM or SMS.
 *
 * In-app DM: POST /api/chat/dm  { operator_id, message }
 * SMS fallback: POST /api/phonehub/sms/send  { to, message }
 */
export default class ContactOperatorModal extends View {
    constructor({ operator }) {
        super();
        this.operator = operator;
        this.sending = false;
        this.channel = 'dm'; // 'dm' | 'sms'
    }

    onActionSetChannel(event, el) {
        this.channel = el.getAttribute('data-channel');
        this.render();
    }

    async onActionSend() {
        const message = this.element.querySelector('[name=message]')?.value.trim();
        if (!message) { app.toast.warning('Enter a message'); return; }
        if (this.sending) return;

        this.sending = true;
        this.render();

        let response;
        if (this.channel === 'sms') {
            const phone = this.operator?.phone;
            if (!phone) {
                app.toast.warning('Operator has no phone number on record');
                this.sending = false;
                this.render();
                return;
            }
            response = await app.api.post('/api/phonehub/sms/send', {
                to: phone,
                message,
            });
        } else {
            response = await app.api.post('/api/chat/dm', {
                operator_id: this.operator?.id,
                message,
            });
        }

        this.sending = false;

        if (!response.success || !response.data?.status) {
            app.toast.error(response.data?.error || 'Failed to send message');
            this.render();
            return;
        }

        app.toast.success('Message sent');
        this.emit('sent');
    }

    get template() {
        const op = this.operator;
        const name = op?.display_name || op?.user?.username || 'Operator';
        const hasPhone = !!op?.phone;

        return `
            <div class="p-3">
                <div class="mb-3 p-3 bg-light rounded d-flex align-items-center gap-2">
                    <i class="bi bi-person-circle fs-4 text-muted"></i>
                    <div>
                        <div class="fw-semibold small">${name}</div>
                        ${op?.user?.email ? `<div class="text-muted small">${op.user.email}</div>` : ''}
                        ${op?.phone ? `<div class="text-muted small"><i class="bi bi-phone me-1"></i>${op.phone}</div>` : ''}
                    </div>
                </div>

                <div class="btn-group btn-group-sm w-100 mb-3" role="group">
                    <button type="button"
                        class="btn ${this.channel === 'dm' ? 'btn-primary' : 'btn-outline-secondary'}"
                        data-action="set-channel" data-channel="dm">
                        <i class="bi bi-chat-dots me-1"></i>In-App
                    </button>
                    <button type="button"
                        class="btn ${this.channel === 'sms' ? 'btn-primary' : 'btn-outline-secondary'} ${!hasPhone ? 'disabled' : ''}"
                        data-action="set-channel" data-channel="sms"
                        title="${!hasPhone ? 'Operator has no phone number' : 'Send SMS'}">
                        <i class="bi bi-phone me-1"></i>SMS
                    </button>
                </div>

                <div class="mb-4">
                    <label class="form-label">Message</label>
                    <textarea class="form-control" name="message" rows="3"
                        placeholder="${this.channel === 'sms' ? 'SMS message…' : 'In-app message…'}"
                        ${this.sending ? 'disabled' : ''}></textarea>
                </div>

                <div class="d-flex justify-content-end">
                    <button type="button" class="btn btn-primary" data-action="send"
                        ${this.sending ? 'disabled' : ''}>
                        ${this.sending
                            ? '<span class="spinner-border spinner-border-sm me-2"></span>Sending…'
                            : `<i class="bi bi-send me-1"></i>Send`}
                    </button>
                </div>
            </div>
        `;
    }

    set template(v) { this._t = v; }
}
